import { useState, useEffect } from 'react';
import { useUser } from '@civic/auth-web3/react';
import { PublicKey } from '@solana/web3.js';
import { logger } from '../utils/logger';
import { failoverConnection as connection } from '../utils/failoverConnection';

// Import config
import { CONFIG } from '../config';

// Solana RPC and token mints
const getRpcUrl = () => {
  const rpcUrl = CONFIG?.RPC_URL;
  if (!rpcUrl) {
    const error = new Error('RPC_URL is not configured. Please set REACT_APP_HELIUS_RPC_URL in your environment variables.');
    logger.error(error.message);
    throw error;
  }
  return rpcUrl;
};

const maskedRpcUrl = (url) => {
  try {
    const urlObj = new URL(url);
    if (urlObj.searchParams.has('api-key')) {
      urlObj.searchParams.set('api-key', '***');
    }
    return urlObj.toString();
  } catch (e) {
    return 'Invalid URL';
  }
};

// Test the connection once on load (best-effort)
connection.getVersion().then(version => {
  logger.log('Connected to Solana (failover):', version);
}).catch(err => {
  logger.warn('Solana connection verification failed (will failover automatically):', err);
});
const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA');
const USDC_MINT = new PublicKey('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'); // Native USDC
const USDT_MINT = new PublicKey('Es9vMFrzaCERBbMERCj2t5Ju6u2RZkMnDNtDG8oYPka');

export function useWallet() {
  const userContext = useUser();
  const user = userContext?.user;
  const authStatus = userContext?.authStatus;
  const authError = userContext?.error;
  const createWalletFunc = userContext?.createWallet;

  // 1. Try to load balances from localStorage first
  const [balances, setBalances] = useState(() => {
    const cached = localStorage.getItem('walletBalances');
    if (cached) {
      // Add SOL if missing from cache
      const parsed = JSON.parse(cached);
      if (!('SOL' in parsed)) parsed.SOL = '0.00';
      return parsed;
    }
    return {
      USDC: '0.00',
      USDT: '0.00',
      SOL: '0.00'
    };
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Civic embedded wallet detection (handles USDC, USDT, SOL, etc)
  // userContext.sol is the Civic embedded Solana wallet object (not SOL the token)
  // It exposes sendTransaction for all SPL tokens, including USDC
  const embeddedWallet = userContext?.sol?.wallet ?? null;
  const directSolWallet = userContext?.sol ?? null;
  const solanaWallet = embeddedWallet || directSolWallet || userContext?.solana || null;
  const wallet = solanaWallet;

  // Auto-create embedded wallet on mount if missing
  useEffect(() => {
    if (createWalletFunc && !solanaWallet) {
      createWalletFunc();
    }
  }, [createWalletFunc, solanaWallet]);

  useEffect(() => {
    if (authStatus === 'authenticated' && user && wallet == null && createWalletFunc) {
      createWalletFunc();
    }
  }, [authStatus, user, wallet, createWalletFunc]);

  // --- Fetch and poll balances ---
  useEffect(() => {
    let isMounted = true;
    let pollInterval;
    if (!wallet?.address) return;

    const fetchBalances = async () => {
      try {
        setIsLoading(true);
        const ownerPubkey = new PublicKey(wallet.address);
        // Fetch SOL balance
        const solBalanceLamports = await connection.getBalance(ownerPubkey);
        const solBalance = solBalanceLamports / 1e9; // Convert lamports to SOL
        // Fetch USDC balance (rate-limit safe)
        let usdcAmt = 0;
        try {
          const usdcResp = await connection.getParsedTokenAccountsByOwner(ownerPubkey, {
            mint: USDC_MINT,
            programId: TOKEN_PROGRAM_ID
          });
          if (usdcResp.value.length > 0) {
            usdcAmt = usdcResp.value[0]?.account?.data?.parsed?.info?.tokenAmount?.uiAmount || 0;
          }
        } catch (e) {
          logger.error('Error fetching USDC token account:', e);
        }
        // Fetch USDT balance (rate-limit safe)
        let usdtAmt = 0;
        try {
          const usdtResp = await connection.getParsedTokenAccountsByOwner(ownerPubkey, {
            mint: USDT_MINT,
            programId: TOKEN_PROGRAM_ID
          });
          if (usdtResp.value.length > 0) {
            usdtAmt = usdtResp.value[0]?.account?.data?.parsed?.info?.tokenAmount?.uiAmount || 0;
          }
        } catch (e) {
          logger.error('Error fetching USDT token account:', e);
        }
        const newBalances = {
          USDC: usdcAmt.toFixed(2),
          USDT: usdtAmt.toFixed(2),
          SOL: solBalance.toFixed(4)
        };
        // Only update if changed
        if (isMounted && JSON.stringify(newBalances) !== JSON.stringify(balances)) {
          setBalances(newBalances);
          localStorage.setItem('walletBalances', JSON.stringify(newBalances));
        }
      } catch (err) {
        logger.error('Failed to fetch balances:', err);
        setError('Failed to fetch balances. Please try again.');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    // Initial fetch
    fetchBalances();
    // Poll every 30 seconds
    pollInterval = setInterval(fetchBalances, 30000);

    return () => {
      isMounted = false;
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [wallet?.address]);

  return {
    wallet,
    isLoading,
    error: authError || error,
    balances
  };
}